import * as THREE from 'three'
import { SCENE, COURT } from '../constants.js'

// ============================================================================
//  Lighting — rig oświetlenia z płynną interpolacją dzień <-> noc.
//  Dzień: słońce (DirectionalLight) + hemisphere fill.
//  Noc:   przygaszone światła globalne w chłodnym tonie („księżyc").
//  Ekspozycja, mgła i kolory interpolowane przez `mix` (0 dzień, 1 noc).
// ============================================================================

const lerp = (a, b, t) => a + (b - a) * t

export class Lighting {
  constructor(scene) {
    this.scene = scene
    this.mix = 0          // bieżąca wartość (0 dzień, 1 noc)
    this.target = 0       // docelowa
    this._speed = 1.4     // tempo przejścia

    // Kierunek słońca (jednostkowy) — używany też przez SkyDome.
    this.sunDir = new THREE.Vector3(0.55, 0.92, 0.42).normalize()

    // --- Słońce ---
    this.sun = new THREE.DirectionalLight(0xfff2dd, 3.4)
    this.sun.position.copy(this.sunDir).multiplyScalar(45)
    this.sun.castShadow = true
    this.sun.shadow.mapSize.set(1024, 1024)
    this.sun.shadow.camera.near = 1
    this.sun.shadow.camera.far = 60
    this.sun.shadow.camera.left = -6
    this.sun.shadow.camera.right = 6
    this.sun.shadow.camera.top = 10
    this.sun.shadow.camera.bottom = -4
    this.sun.shadow.bias = -0.0004
    this.sun.shadow.normalBias = 0.02
    this.sun.shadow.radius = 4
    this.sun.target.position.set(0, 0, COURT.HALF_LENGTH * 0.45)
    scene.add(this.sun)
    scene.add(this.sun.target)

    // --- Hemisphere (wypełnienie niebo/ziemia) ---
    this.hemi = new THREE.HemisphereLight(0xbcd6ff, 0x6b4a2f, 0.55)
    scene.add(this.hemi)

    // --- Ambient (delikatne podbicie cieni) ---
    this.ambient = new THREE.AmbientLight(0xffffff, 0.22)
    scene.add(this.ambient)

    // --- Mgła (subtelna głębia) — JASNA, pod białe tło ---
    this.fog = new THREE.FogExp2(0xe9edf2, 0.006)
    scene.fog = this.fog
    this._dayFog = new THREE.Color(0xe9edf2)
    this._nightFog = new THREE.Color(0x0a0d14)

    this._apply()
  }

  setMode(mode) {
    this.target = mode === 'night' ? 1 : 0
  }

  toggle() {
    const next = this.target >= 0.5 ? 'day' : 'night'
    this.setMode(next)
    return next
  }

  // Wartości interpolowane na podstawie this.mix.
  _apply() {
    const m = this.mix
    this.sun.intensity = lerp(3.4, 0.5, m)
    this.hemi.intensity = lerp(0.55, 0.22, m)
    this.ambient.intensity = lerp(0.22, 0.10, m)
    this.hemi.color.setHSL(0.6, 0.6, lerp(0.85, 0.25, m))
    this.fog.color.copy(this._dayFog).lerp(this._nightFog, m)
    this.fog.density = lerp(0.006, 0.02, m)
  }

  get exposure() {
    return lerp(SCENE.DAY_EXPOSURE, SCENE.NIGHT_EXPOSURE, this.mix)
  }

  // Natychmiastowe ustawienie trybu (bez animacji przejścia) — np. tryb startowy.
  forceMix(m) {
    this.mix = m
    this.target = m
    this._apply()
  }

  // Dopasowuje kamerę cieni słońca i cel do rozmiaru płyty (court.bounds).
  // Bez tego cień zostałby ucięty przy większym boisku.
  updateBounds(bounds) {
    const r = 0.5 * Math.hypot(bounds.width, bounds.length) + 1.5
    const cam = this.sun.shadow.camera
    cam.left = -r; cam.right = r
    cam.top = r; cam.bottom = -r
    cam.far = 120
    cam.updateProjectionMatrix()
    this.sun.position.copy(this.sunDir).multiplyScalar(60)
    this.sun.position.z += bounds.centerZ
    this.sun.target.position.set(0, 0, bounds.centerZ)
    this.sun.target.updateMatrixWorld()
  }

  update(dt) {
    if (Math.abs(this.mix - this.target) > 0.0005) {
      const dir = Math.sign(this.target - this.mix)
      this.mix = THREE.MathUtils.clamp(this.mix + dir * this._speed * dt, 0, 1)
      this._apply()
    }
  }
}
